import type { ComponentPropsWithoutRef } from 'react';
import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { formatAmountWithCurrency } from '@fixter/i18n';
import type { ContentComponent, ContentComponentSchema } from '$cms/ContentComponent';
import { useAppContext } from '$util/AppContext';
import type { AppContextGlobal } from '$util/AppContextGlobal';
import { getSegmentFromLocale } from '$util/getSegment';
import { useModalQueue } from '$util/hooks/useModalQueue';
import { ComparisonTable } from '@/components/molecules/ComparisonTable';
import { ContentComponentSection } from '@/components/atoms/ContentComponentSection';
import { Col, Row } from '@/components/atoms/Grid';
import { ServiceCard } from '@/components/molecules/ServiceCard';
import { Card } from '@/components/atoms/Card';
import { Icon } from '@/components/atoms/Icon';
import { FixieInfo } from '@/components/molecules/FixieInfo';
import { Button, DesignType } from '@/components/atoms/Button';
import { FixieCard } from '@/components/molecules/FixieCard';
import styles from './index.module.scss';
import { CompareServicesSchema } from './schema';
import type { LinkToPageProps, CompareServicesTranslatedEntities } from './schema';

export interface CompareServicesProps extends ComponentPropsWithoutRef<'div'> {}

function LinkToLP({ linkToPageContent, linkToPage, linkToPageCTAText }: LinkToPageProps) {
  return (
    <Row className={styles.linkSection}>
      <Col medium={6} extraLarge={1} className={styles.spacing}>
        &nbsp;
      </Col>
      <Col extraSmall={4} small={6} medium={12} extraLarge={10}>
        <FixieCard className={styles.fixieCard}>
          <div className={styles.fixieCardContent}>
            <h6>{linkToPageContent}</h6>
            <Button
              size="L"
              designType={DesignType.SECONDARY_DARK}
              href={linkToPage}
              className={classNames(styles.accessPageDesktop, styles.accessPage)}
            >
              {linkToPageCTAText}
            </Button>
            <Button
              size="S"
              designType={DesignType.SECONDARY_DARK}
              href={linkToPage}
              className={classNames(styles.accessPageMobile, styles.accessPage)}
            >
              {linkToPageCTAText}
            </Button>
          </div>
        </FixieCard>
      </Col>
      <Col medium={6} extraLarge={1} className={styles.spacing}>
        &nbsp;
      </Col>
    </Row>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- exception for allowing transform on the schema
export const CompareServices: ContentComponent<any, null, CompareServicesProps> = function CompareServices({
  translatedEntities,
  className,
  ...props
}): JSX.Element {
  const { locale } = useAppContext<AppContextGlobal>();
  const {
    ourOfferText,
    mostPopularText,
    popularText,
    startingFormText,
    getQuoteText,
    compareOffersText,
    linkToPage,
    linkToPageContent,
    linkToPageCTAText,
    services,
    lineItems,
  } = translatedEntities as CompareServicesTranslatedEntities;
  const comparisonTableTranslations = useMemo(() => {
    return {
      ourOffer: ourOfferText.value,
      mostPopular: mostPopularText.value,
      startingFrom: startingFormText.value,
      getQuote: getQuoteText.value,
    };
  }, [getQuoteText.value, mostPopularText.value, ourOfferText.value, startingFormText.value]);

  const segment = getSegmentFromLocale(locale);

  const [, queueModal] = useModalQueue();
  const onCompareClicked = useCallback(() => {
    queueModal(
      <div className={styles.modalContent}>
        <h5 className={styles.modalHeading}>{compareOffersText.value}</h5>
        <ComparisonTable
          translations={comparisonTableTranslations}
          services={services}
          lineItems={lineItems}
          segment={segment}
          forceMobile
        />
      </div>
    );
  }, [compareOffersText.value, comparisonTableTranslations, lineItems, queueModal, segment, services]);
  const labelColors = [styles.labelColor1, styles.labelColor2, styles.labelColor3];

  return (
    <ContentComponentSection
      className={classNames(CompareServices.displayName, styles.component, className)}
      {...props}
    >
      <Row className={styles.comparisonTableDesktop}>
        <Col medium={6} extraLarge={1} className={styles.spacing}>
          &nbsp;
        </Col>
        <Col extraSmall={4} small={6} medium={12} extraLarge={10}>
          <Card elevation={1} cornerRadius={3} className={styles.comparisonTableCard}>
            <ComparisonTable
              translations={comparisonTableTranslations}
              services={services}
              lineItems={lineItems}
              segment={segment}
            />
          </Card>
        </Col>
        <Col medium={6} extraLarge={1} className={styles.spacing}>
          &nbsp;
        </Col>
      </Row>
      <Row className={styles.serviceMobile}>
        <Col className={styles.servicesSection}>
          <h4>{ourOfferText.value}</h4>
          <div className={styles.servicesCards}>
            {services.map(({ name, price, popular, info, description }, index) => (
              <ServiceCard
                title={name}
                label={popular ? popularText.value : ''}
                labelColor={labelColors[index]}
                key={name}
              >
                <div className={styles.servicesContent}>
                  <FixieInfo>{info}</FixieInfo>
                  <div className={styles.description}>{description}</div>
                  <div className={styles.price}>
                    {startingFormText.value}
                    <span>{formatAmountWithCurrency(price, segment, 0)}</span>
                  </div>
                </div>
              </ServiceCard>
            ))}
          </div>
          <Card elevation={1} cornerRadius={3} className={styles.compareServices} onClick={onCompareClicked}>
            <div className={styles.compareIcon}>
              <Icon id="arrows/switch-horizontal-02" legacy={false} width={20} height={20} />
            </div>
            {compareOffersText.value}
          </Card>
        </Col>
      </Row>
      {linkToPageContent && linkToPage && linkToPageCTAText && (
        <LinkToLP
          linkToPageContent={linkToPageContent.value}
          linkToPage={linkToPage.value}
          linkToPageCTAText={linkToPageCTAText.value}
        />
      )}
    </ContentComponentSection>
  );
};
CompareServices.displayName = 'CompareServices';
CompareServices.schema = CompareServicesSchema as unknown as ContentComponentSchema;
